import React, { useEffect, useState } from "react";
import { api, formatApiError, API } from "@/api";
import { PageHeader } from "@/pages/admin/AdminDashboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { toast } from "sonner";
import { ShieldCheck, Plus, KeyRound, Ban, RefreshCw, Trash2, ScrollText, Activity, AlertOctagon, Smartphone, AppWindow, Plug, Webhook } from "lucide-react";
import { CredentialsDialog } from "@/components/chef/SecureConnectCredentials";
import { WebhookSheet } from "@/components/chef/SecureConnectWebhook";

const KIND_ICON = { device: Smartphone, application: AppWindow, integration: Plug };
const KIND_LABEL = { device: "Appareil", application: "Application", integration: "Intégration" };
const STATE = { active: ["Actif", "status-active"], revoked: ["Révoqué", "status-inactive"], expired: ["Expiré", "status-pending"] };
const fmt = (d) => (d ? new Date(d).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }) : "—");
const EMPTY = { name: "", kind: "application", scopes: ["planning:read"], expires_in_days: 365, notes: "" };

export default function ChefSecurityCenter() {
  const [data, setData] = useState(null);
  const [dialog, setDialog] = useState(null); // { mode: create|edit, form, device }
  const [creds, setCreds] = useState(null);
  const [revoke, setRevoke] = useState(null); // { device, reason } | { all: true, reason }
  const [logs, setLogs] = useState(null);
  const [webhook, setWebhook] = useState(null); // device

  const load = async () => {
    try { const { data: r } = await api.get("/chef/secure-connect"); setData(r); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const act = async (fn, msg) => { try { const r = await fn(); if (msg) toast.success(msg); load(); return r; } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  const save = async () => {
    const f = dialog.form;
    const body = { name: f.name, kind: f.kind, scopes: f.scopes, expires_in_days: Number(f.expires_in_days), notes: f.notes };
    try {
      if (dialog.mode === "create") {
        const { data: r } = await api.post("/chef/secure-connect/devices", body);
        setDialog(null); setCreds({ ...r.credentials, name: r.device.name, title: "Appareil enregistré" });
      } else {
        await api.patch(`/chef/secure-connect/devices/${dialog.device.id}`, body);
        toast.success("Appareil mis à jour"); setDialog(null);
      }
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const doRevoke = async () => {
    const reason = revoke.reason || "";
    if (revoke.all) {
      const r = await act(() => api.post("/chef/secure-connect/revoke-all", { reason }));
      if (r) toast.success(`${r.data.revoked} appareil(s) révoqué(s)`);
    } else {
      await act(() => api.post(`/chef/secure-connect/devices/${revoke.device.id}/revoke`, { reason }), `${revoke.device.device_id} révoqué — accès coupé immédiatement`);
    }
    setRevoke(null);
  };

  const rotate = async (d) => {
    if (!window.confirm(`Régénérer la clé et le secret de ${d.device_id} ? Les anciens identifiants cesseront de fonctionner immédiatement.`)) return;
    try { const { data: r } = await api.post(`/chef/secure-connect/devices/${d.id}/rotate`); setCreds({ ...r.credentials, name: d.name, title: "Nouveaux identifiants" }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openLogs = async (d) => {
    try { const { data: r } = await api.get(`/chef/secure-connect/devices/${d.id}/logs`); setLogs(r); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggleScope = (k) => setDialog((s) => ({ ...s, form: { ...s.form, scopes: s.form.scopes.includes(k) ? s.form.scopes.filter((x) => x !== k) : [...s.form.scopes, k] } }));
  const scopeLabel = (k) => data?.scopes.find((s) => s.key === k)?.label || k;

  return (
    <div className="min-h-full" data-testid="chef-security-center">
      <PageHeader eyebrow="SecureConnect" title="Security Center"
        sub="Enregistrez les appareils, logiciels ou intégrations autorisés à lire vos données via l'API partenaire. Chaque appareil reçoit un Device ID, une clé API et un secret (affichés une seule fois), des portées limitées, une date d'expiration et peut être révoqué en un clic."
        right={
          <div className="flex gap-2">
            {data?.stats.active > 0 && <Button variant="outline" onClick={() => setRevoke({ all: true, reason: "" })} data-testid="sc-revoke-all" className="h-11 border-[#E60000] text-[#E60000] hover:bg-[#E60000] hover:text-white font-semibold"><AlertOctagon className="h-4 w-4 mr-2" /> Tout révoquer</Button>}
            <Button onClick={() => setDialog({ mode: "create", form: { ...EMPTY } })} data-testid="sc-add-device" className="h-11 bg-[#002FA7] hover:bg-[#002175] text-white font-semibold"><Plus className="h-4 w-4 mr-2" /> Nouvel appareil</Button>
          </div>
        } />

      <div className="p-6 lg:p-10 space-y-6">
        {data && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
            <Kpi label="Appareils actifs" value={data.stats.active} icon={ShieldCheck} dark testid="sc-kpi-active" />
            <Kpi label="Révoqués" value={data.stats.revoked} icon={Ban} testid="sc-kpi-revoked" />
            <Kpi label="Expirés" value={data.stats.expired} icon={KeyRound} testid="sc-kpi-expired" />
            <Kpi label="Appels 30 jours" value={data.stats.calls_30d} icon={Activity} testid="sc-kpi-calls" />
            <Kpi label="Refusés 30 jours" value={data.stats.denied_30d} icon={AlertOctagon} accent="#E60000" testid="sc-kpi-denied" />
          </div>
        )}

        <div className="bg-white border border-[#DEE2E6]" data-testid="sc-device-list">
          <div className="grid grid-cols-12 gap-3 px-4 py-2.5 text-[10px] uppercase tracking-[0.2em] font-bold text-[#868E96] bg-[#F8F9FA] border-b border-[#DEE2E6]">
            <div className="col-span-3">Appareil</div><div className="col-span-3">Portées</div><div className="col-span-2">Expiration</div><div className="col-span-2">Dernier appel</div><div className="col-span-2 text-right">Actions</div>
          </div>
          {!data || data.devices.length === 0 ? (
            <div className="p-10 text-center text-sm text-[#868E96]" data-testid="sc-empty">Aucun appareil enregistré. Cliquez sur « Nouvel appareil » pour générer un Device ID, une clé API et un secret.</div>
          ) : data.devices.map((d) => {
            const Icon = KIND_ICON[d.kind] || AppWindow;
            const [stLabel, stCls] = STATE[d.state] || STATE.active;
            return (
              <div key={d.id} className="grid grid-cols-12 gap-3 px-4 py-3 items-center border-b border-[#F1F3F5] last:border-b-0 text-sm" data-testid={`sc-device-${d.device_id}`}>
                <div className="col-span-3 flex items-start gap-2">
                  <Icon className="h-4 w-4 mt-0.5 text-[#002FA7] shrink-0" />
                  <div>
                    <div className="font-semibold">{d.name} <span className={`status-pill ${stCls} ml-1`} data-testid={`sc-state-${d.device_id}`}>{stLabel}</span></div>
                    <div className="font-mono text-[11px] text-[#868E96]">{d.device_id} · {d.key_prefix}</div>
                    <div className="text-[10px] text-[#868E96]">{KIND_LABEL[d.kind]} · {d.usage_count} appel(s){d.rotated_at ? " · clé régénérée" : ""}</div>
                    {d.webhook?.url && <div className={`text-[10px] font-semibold inline-flex items-center gap-1 mt-0.5 ${d.webhook.enabled === false ? "text-[#868E96]" : d.webhook.last_status === "failed" ? "text-[#E60000]" : "text-[#002FA7]"}`} data-testid={`sc-webhook-badge-${d.device_id}`}><Webhook className="h-3 w-3" />Webhook {d.webhook.enabled === false ? "suspendu" : d.webhook.last_status === "failed" ? "en échec" : d.webhook.last_status === "delivered" ? "OK" : "actif"} · {(d.webhook.events || []).length} évt</div>}
                  </div>
                </div>
                <div className="col-span-3 flex flex-wrap gap-1">{d.scopes.map((s) => <span key={s} className={`text-[10px] border px-1.5 py-0.5 font-mono ${s.endsWith(":write") ? "border-[#FFD700] bg-[#FFD700]/20 text-[#7a5c00]" : "border-[#002FA7]/30 text-[#002FA7]"}`}>{s}</span>)}</div>
                <div className="col-span-2 text-xs">{d.expires_at ? fmt(d.expires_at) : <span className="text-[#868E96]">Sans expiration</span>}</div>
                <div className="col-span-2 text-xs">{d.last_used_at ? <>{fmt(d.last_used_at)}<div className="font-mono text-[10px] text-[#868E96]">{d.last_used_ip}</div></> : <span className="text-[#868E96]">Jamais</span>}</div>
                <div className="col-span-2 flex justify-end gap-1">
                  <Button size="sm" variant="outline" onClick={() => openLogs(d)} data-testid={`sc-logs-${d.device_id}`} className="h-8 px-2" title="Journal"><ScrollText className="h-3.5 w-3.5" /></Button>
                  {d.state !== "revoked" && <Button size="sm" variant="outline" onClick={() => setWebhook(d)} data-testid={`sc-webhook-${d.device_id}`} className={`h-8 px-2 ${d.webhook?.url ? "border-[#002FA7] text-[#002FA7]" : ""}`} title="Webhook (notifications sortantes)"><Webhook className="h-3.5 w-3.5" /></Button>}
                  {d.state !== "revoked" && <Button size="sm" variant="outline" onClick={() => setDialog({ mode: "edit", device: d, form: { name: d.name, kind: d.kind, scopes: d.scopes, expires_in_days: d.expires_at ? 365 : 0, notes: d.notes || "" } })} data-testid={`sc-edit-${d.device_id}`} className="h-8 px-2" title="Modifier"><KeyRound className="h-3.5 w-3.5" /></Button>}
                  {d.state !== "revoked" && <Button size="sm" variant="outline" onClick={() => rotate(d)} data-testid={`sc-rotate-${d.device_id}`} className="h-8 px-2 border-[#FFD700] hover:bg-[#FFD700]" title="Régénérer clé + secret"><RefreshCw className="h-3.5 w-3.5" /></Button>}
                  {d.state !== "revoked" && <Button size="sm" variant="outline" onClick={() => setRevoke({ device: d, reason: "" })} data-testid={`sc-revoke-${d.device_id}`} className="h-8 px-2 border-[#E60000] text-[#E60000] hover:bg-[#E60000] hover:text-white" title="Révoquer"><Ban className="h-3.5 w-3.5" /></Button>}
                  {d.state !== "active" && <Button size="sm" variant="outline" onClick={() => { if (window.confirm(`Supprimer définitivement ${d.device_id} ?`)) act(() => api.delete(`/chef/secure-connect/devices/${d.id}`), "Appareil supprimé"); }} data-testid={`sc-delete-${d.device_id}`} className="h-8 px-2" title="Supprimer"><Trash2 className="h-3.5 w-3.5" /></Button>}
                </div>
              </div>
            );
          })}
        </div>

        {data && (
          <div className="grid lg:grid-cols-2 gap-6">
            <div className="bg-[#0A0A0A] text-white p-6" data-testid="sc-docs">
              <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#FFD700] mb-3">API partenaire</div>
              <div className="text-xs text-white/70 mb-3">Base : <span className="font-mono text-white">{API}/v1</span> · en-têtes <span className="font-mono text-white">X-Device-Id</span>, <span className="font-mono text-white">X-API-Key</span>, <span className="font-mono text-white">X-API-Secret</span> · limite {data.rate_limit_per_min} requêtes / minute / appareil.</div>
              <div className="space-y-1.5">
                {data.endpoints.map((e) => (
                  <div key={e.path} className="flex items-start gap-2 text-xs">
                    <span className="font-mono text-[#FFD700] w-8 shrink-0">{e.method}</span>
                    <div className="min-w-0"><div className="font-mono truncate">{e.path}</div><div className="text-white/50">{e.desc}{e.scope ? ` · portée ${e.scope}` : ""}</div></div>
                  </div>
                ))}
              </div>
              {data.webhook_events && (
                <div className="mt-4 pt-3 border-t border-white/15" data-testid="sc-docs-webhooks">
                  <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#FFD700] mb-2 flex items-center gap-1"><Webhook className="h-3 w-3" /> Webhooks sortants (par appareil)</div>
                  <div className="space-y-1">{data.webhook_events.map((e) => <div key={e.key} className="text-xs flex items-start gap-2"><span className="font-mono text-white shrink-0">{e.key}</span><span className="text-white/50">{e.label} · portée {e.scope}</span></div>)}</div>
                  <div className="text-white/50 text-xs mt-2">Signature HMAC-SHA256 (<span className="font-mono">X-PAG-Signature</span>), 3 tentatives, journal des livraisons — bouton <Webhook className="h-3 w-3 inline" /> sur chaque appareil.</div>
                </div>
              )}
            </div>
            <div className="bg-white border border-[#DEE2E6] p-6" data-testid="sc-recent">
              <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#868E96] mb-3">Derniers appels (toutes clés)</div>
              {data.recent_calls.length === 0 ? <div className="text-sm text-[#868E96]">Aucun appel enregistré.</div> : (
                <div className="space-y-1">
                  {data.recent_calls.map((l) => <LogRow key={l.id} l={l} />)}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Création / édition */}
      <Dialog open={!!dialog} onOpenChange={(o) => { if (!o) setDialog(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="sc-device-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl font-black">{dialog?.mode === "create" ? "Nouvel appareil / application" : `Modifier ${dialog?.device?.device_id}`}</DialogTitle></DialogHeader>
          {dialog && data && (
            <div className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-[10px] uppercase tracking-[0.2em] font-semibold">Nom *</Label>
                  <Input value={dialog.form.name} placeholder="Caisse accueil, logiciel comptable, Zapier…" onChange={(e) => setDialog({ ...dialog, form: { ...dialog.form, name: e.target.value } })} data-testid="sc-form-name" className="h-11" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[10px] uppercase tracking-[0.2em] font-semibold">Type</Label>
                  <Select value={dialog.form.kind} onValueChange={(v) => setDialog({ ...dialog, form: { ...dialog.form, kind: v } })}>
                    <SelectTrigger data-testid="sc-form-kind" className="h-11"><SelectValue /></SelectTrigger>
                    <SelectContent>{data.kinds.map((k) => <SelectItem key={k} value={k}>{KIND_LABEL[k]}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[10px] uppercase tracking-[0.2em] font-semibold">Expiration</Label>
                  <Select value={String(dialog.form.expires_in_days)} onValueChange={(v) => setDialog({ ...dialog, form: { ...dialog.form, expires_in_days: v } })}>
                    <SelectTrigger data-testid="sc-form-expiry" className="h-11"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="30">30 jours</SelectItem><SelectItem value="90">90 jours</SelectItem>
                      <SelectItem value="365">1 an (recommandé)</SelectItem><SelectItem value="0">Sans expiration</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[10px] uppercase tracking-[0.2em] font-semibold">Notes</Label>
                  <Textarea value={dialog.form.notes} onChange={(e) => setDialog({ ...dialog, form: { ...dialog.form, notes: e.target.value } })} data-testid="sc-form-notes" rows={1} className="min-h-[44px]" />
                </div>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.2em] font-semibold">Portées (données accessibles) *</Label>
                <div className="grid sm:grid-cols-2 gap-2 mt-2" data-testid="sc-form-scopes">
                  {data.scopes.map((s) => {
                    const on = dialog.form.scopes.includes(s.key);
                    return (
                      <button type="button" key={s.key} onClick={() => toggleScope(s.key)} data-testid={`sc-scope-${s.key}`} aria-pressed={on}
                        className={`text-left p-3 border transition-colors ${on ? "border-[#002FA7] bg-[#002FA7]/5" : "border-[#DEE2E6] hover:border-[#0A0A0A]"}`}>
                        <div className="text-sm font-semibold flex items-center gap-2"><span className={`h-3 w-3 border ${on ? "bg-[#002FA7] border-[#002FA7]" : "border-[#868E96]"}`} />{s.label}{s.write && <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 bg-[#FFD700] text-black" data-testid={`sc-scope-write-${s.key}`}>écriture</span>}</div>
                        <div className="text-[11px] text-[#868E96] mt-0.5">{s.desc} <span className="font-mono">({s.key})</span></div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)}>Annuler</Button>
            <Button onClick={save} disabled={!dialog || dialog.form.name.trim().length < 2 || dialog.form.scopes.length === 0} data-testid="sc-form-save" className="bg-[#002FA7] hover:bg-[#002175] text-white">{dialog?.mode === "create" ? "Générer les identifiants" : "Enregistrer"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CredentialsDialog creds={creds} onClose={() => setCreds(null)} />
      <WebhookSheet device={webhook} onClose={() => setWebhook(null)} onChanged={load} />

      {/* Révocation */}
      <Dialog open={!!revoke} onOpenChange={(o) => { if (!o) setRevoke(null); }}>
        <DialogContent data-testid="sc-revoke-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl font-black flex items-center gap-2"><Ban className="h-5 w-5 text-[#E60000]" /> {revoke?.all ? "Révoquer tous les appareils" : `Révoquer ${revoke?.device?.device_id}`}</DialogTitle></DialogHeader>
          {revoke && (
            <div className="space-y-3">
              <div className="p-3 bg-[#FEF2F2] border border-[#FECACA] text-sm text-[#991B1B]">{revoke.all ? "Tous les appareils actifs perdront l'accès immédiatement. Action irréversible : il faudra recréer des identifiants." : `« ${revoke.device.name} » perdra l'accès immédiatement. Action irréversible.`}</div>
              <div className="space-y-1.5">
                <Label className="text-[10px] uppercase tracking-[0.2em] font-semibold">Motif (optionnel)</Label>
                <Input value={revoke.reason} onChange={(e) => setRevoke({ ...revoke, reason: e.target.value })} data-testid="sc-revoke-reason" placeholder="Appareil perdu, prestataire terminé…" className="h-11" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRevoke(null)}>Annuler</Button>
            <Button onClick={doRevoke} data-testid="sc-revoke-confirm" className="bg-[#E60000] hover:bg-[#B80000] text-white">Révoquer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Journal */}
      <Sheet open={!!logs} onOpenChange={(o) => { if (!o) setLogs(null); }}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid="sc-logs-sheet">
          <SheetHeader><SheetTitle className="font-display text-xl font-black">Journal — {logs?.name} <span className="font-mono text-sm text-[#868E96]">{logs?.device_id}</span></SheetTitle></SheetHeader>
          <div className="mt-4 space-y-1">
            {logs?.logs.length === 0 && <div className="text-sm text-[#868E96]" data-testid="sc-logs-empty">Aucun appel enregistré pour cet appareil.</div>}
            {logs?.logs.map((l) => <LogRow key={l.id} l={l} />)}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}

function Kpi({ label, value, icon: Icon, dark, accent, testid }) {
  return (
    <div className={`p-5 border ${dark ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white border-[#DEE2E6]"}`} data-testid={testid}>
      <div className={`flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] font-bold ${dark ? "text-[#FFD700]" : "text-[#868E96]"}`}><Icon className="h-3 w-3" /> {label}</div>
      <div className="font-display text-2xl font-black mt-2" style={accent && !dark ? { color: accent } : {}}>{value}</div>
    </div>
  );
}

function LogRow({ l }) {
  const ok = l.status < 400;
  return (
    <div className="flex items-center gap-2 text-xs border-b border-[#F1F3F5] py-1.5 last:border-b-0" data-testid={`sc-log-${l.id}`}>
      <span className={`font-mono font-bold w-8 ${ok ? "text-[#059669]" : "text-[#E60000]"}`}>{l.status}</span>
      <span className="font-mono text-[#868E96] w-24 shrink-0">{l.device_id}</span>
      <span className="font-mono truncate flex-1">{l.method} {l.path}</span>
      <span className="text-[#868E96] shrink-0">{fmt(l.at)}</span>
    </div>
  );
}

